/**
 * PWA helpers — service-worker registration, push subscription,
 * install prompt handling.
 *
 * Everything in this module is best-effort — a browser that can't
 * install PWAs or refuses notification permission MUST NOT break the
 * rest of the app. All errors are swallowed at the call site.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const b = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
    return reg;
  } catch (e) {
    console.warn('[PWA] SW registration failed', e);
    return null;
  }
}

export async function subscribeToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await axios.get(`${API}/push/public-key`);
      const key = data?.public_key;
      if (!key) return { ok: false, reason: 'no-key' };
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
    await axios.post(`${API}/push/subscribe`, sub.toJSON(), { withCredentials: true });
    return { ok: true };
  } catch (e) {
    console.warn('[PWA] push subscribe failed', e);
    return { ok: false, reason: 'error', error: e };
  }
}

/**
 * Ask the browser for notification permission if we haven't yet, then
 * subscribe. Returns the granted/denied/default status.
 */
export async function ensurePushEnabled() {
  if (!('Notification' in window)) return { granted: false, reason: 'unsupported' };
  let perm = Notification.permission;
  if (perm === 'default') {
    try {
      perm = await Notification.requestPermission();
    } catch { perm = 'denied'; }
  }
  if (perm !== 'granted') return { granted: false, reason: perm };
  const sub = await subscribeToPush();
  return { granted: sub.ok, reason: sub.ok ? 'subscribed' : sub.reason };
}

export function permissionState() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}
